import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, Rect, Textbox, FabricImage } from 'fabric';
import manualEditorService from '../services/manualEditorService';

const CANVAS_SIZE = 1080;

const newLayerId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `layer-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

function normalizeObject(object) {
  if (!object.get('mpObjectId')) {
    object.set('mpObjectId', newLayerId());
  }
  return object;
}

export default function useManualEditor({ initialDesignId = null, initialCanvasJson = null, initialImageUrl = null }) {
  const canvasElementRef = useRef(null);
  const canvasRef = useRef(null);

  const [isReady, setIsReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [designId, setDesignId] = useState(initialDesignId || null);
  const [layers, setLayers] = useState([]);
  const [activeLayerId, setActiveLayerId] = useState(null);
  const [selectedPhotoMedia, setSelectedPhotoMedia] = useState(null);
  const [selectedVideoMedia, setSelectedVideoMedia] = useState(null);
  const [videoSourceUrl, setVideoSourceUrl] = useState('');
  const [textStyle, setTextStyle] = useState({
    family: 'Inter',
    weight: '400',
    size: 52,
    spacing: 0,
    preview_url: '',
  });

  const loadFontCss = useCallback((previewUrl) => {
    if (!previewUrl) return;
    const key = `manual-font-${encodeURIComponent(previewUrl)}`;
    const exists = document.querySelector(`link[data-mp-font="${key}"]`);
    if (exists) return;

    const link = document.createElement('link');
    link.href = previewUrl;
    link.rel = 'stylesheet';
    link.dataset.mpFont = key;
    document.head.appendChild(link);
  }, []);

  const themeColors = useMemo(() => {
    const rootStyles = window.getComputedStyle(document.documentElement);
    const bg = rootStyles.getPropertyValue('--surface-2')?.trim() || '#0f172a';
    const fg = rootStyles.getPropertyValue('--text-primary')?.trim() || '#ffffff';
    return { bg, fg };
  }, []);

  const refreshLayers = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const objects = canvas.getObjects();
    const normalized = objects
      .filter((object) => object.get('selectable') !== false)
      .map((object, index) => {
        normalizeObject(object);
        return {
          id: object.get('mpObjectId'),
          name: object.type === 'textbox' ? `Texto ${index + 1}` : `Camada ${index + 1}`,
          type: object.type,
        };
      });

    setLayers(normalized.reverse());
    const active = canvas.getActiveObject();
    setActiveLayerId(active?.get('mpObjectId') || null);

    if (active && (active.type === 'textbox' || active.type === 'text')) {
      setTextStyle((previous) => ({
        ...previous,
        family: active.fontFamily || previous.family,
        weight: String(active.fontWeight || previous.weight || '400'),
        size: Number(active.fontSize || previous.size || 52),
        spacing: Number(active.charSpacing || previous.spacing || 0),
      }));
    }
  }, []);

  const addBackground = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const alreadyHasBackground = canvas
      .getObjects()
      .some((object) => object.get('mpRole') === 'background');

    if (alreadyHasBackground) return;

    const background = new Rect({
      left: 0,
      top: 0,
      width: CANVAS_SIZE,
      height: CANVAS_SIZE,
      fill: themeColors.bg,
      selectable: false,
      evented: false,
    });
    background.set('mpRole', 'background');
    canvas.add(background);

    if (typeof canvas.sendObjectToBack === 'function') {
      canvas.sendObjectToBack(background);
    } else if (typeof background.sendToBack === 'function') {
      background.sendToBack();
    }

    canvas.requestRenderAll();
  }, [themeColors.bg]);

  const loadCanvasJson = useCallback((canvasJson) => {
    const canvas = canvasRef.current;
    if (!canvas || !canvasJson) return;

    canvas.loadFromJSON(canvasJson, () => {
      const objects = canvas.getObjects();
      objects.forEach((object) => normalizeObject(object));
      canvas.renderAll();
      refreshLayers();
    });
  }, [refreshLayers]);

  useEffect(() => {
    if (!canvasElementRef.current) return;

    if (canvasRef.current) {
      canvasRef.current.dispose();
      canvasRef.current = null;
    }

    const canvas = new Canvas(canvasElementRef.current, {
      width: CANVAS_SIZE,
      height: CANVAS_SIZE,
      preserveObjectStacking: true,
      selection: true,
    });

    canvasRef.current = canvas;
    addBackground();

    canvas.on('selection:created', refreshLayers);
    canvas.on('selection:updated', refreshLayers);
    canvas.on('selection:cleared', refreshLayers);
    canvas.on('object:added', refreshLayers);
    canvas.on('object:removed', refreshLayers);
    canvas.on('object:modified', refreshLayers);

    if (initialCanvasJson) {
      loadCanvasJson(initialCanvasJson);
    } else {
      const initialText = new Textbox('Criação Manual', {
        left: 80,
        top: 90,
        width: 560,
        fontSize: 72,
        fontWeight: textStyle.weight,
        fontFamily: textStyle.family,
        fill: themeColors.fg,
        charSpacing: textStyle.spacing,
      });
      normalizeObject(initialText);
      canvas.add(initialText);
      refreshLayers();
    }

    setIsReady(true);

    return () => {
      canvas.dispose();
      canvasRef.current = null;
    };
  }, [addBackground, initialCanvasJson, loadCanvasJson, refreshLayers, textStyle.family, textStyle.spacing, textStyle.weight, themeColors.fg]);

  useEffect(() => {
    if (!initialDesignId || !canvasRef.current) return;

    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await manualEditorService.getDesign(initialDesignId);
        if (data?.success && data.design?.canvas_json) {
          setDesignId(data.design.id);
          loadCanvasJson(data.design.canvas_json);
        }
      } catch (requestError) {
        setError('Não foi possível carregar o design para edição.');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [initialDesignId, loadCanvasJson]);

  const addText = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const text = new Textbox('Novo texto', {
      left: 120,
      top: 180,
      width: 420,
      fontSize: textStyle.size,
      fill: themeColors.fg,
      fontFamily: textStyle.family,
      fontWeight: textStyle.weight,
      charSpacing: textStyle.spacing,
    });
    normalizeObject(text);
    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.requestRenderAll();
  }, [textStyle.family, textStyle.size, textStyle.spacing, textStyle.weight, themeColors.fg]);

  const applyTextStyle = useCallback((nextStyle) => {
    setTextStyle((previous) => {
      const merged = { ...previous, ...nextStyle };
      if (merged.preview_url) {
        loadFontCss(merged.preview_url);
      }

      const canvas = canvasRef.current;
      const active = canvas?.getActiveObject();
      if (canvas && active && (active.type === 'textbox' || active.type === 'text')) {
        active.set({
          fontFamily: merged.family,
          fontWeight: String(merged.weight),
          fontSize: Number(merged.size),
          charSpacing: Number(merged.spacing),
        });
        canvas.requestRenderAll();
      }

      return merged;
    });
  }, [loadFontCss]);

  const addImageFromUrl = useCallback(async (url) => {
    const canvas = canvasRef.current;
    if (!canvas || !url) return;

    try {
      const image = await FabricImage.fromURL(url, { crossOrigin: 'anonymous' });
      normalizeObject(image);
      const maxWidth = 460;
      if (image.width && image.width > maxWidth) {
        image.scaleToWidth(maxWidth);
      }
      image.set({ left: 560, top: 220 });
      canvas.add(image);
      canvas.setActiveObject(image);
      canvas.requestRenderAll();
    } catch {
      setError('Falha ao inserir imagem.');
    }
  }, []);

  const addVideoFromUrl = useCallback(async (url) => {
    if (!url) return;

    const video = document.createElement('video');
    video.src = url;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    try {
      await new Promise((resolve, reject) => {
        video.addEventListener('loadeddata', resolve, { once: true });
        video.addEventListener('error', reject, { once: true });
      });

      const frameCanvas = document.createElement('canvas');
      frameCanvas.width = video.videoWidth || 1280;
      frameCanvas.height = video.videoHeight || 720;
      const frameContext = frameCanvas.getContext('2d');
      frameContext?.drawImage(video, 0, 0, frameCanvas.width, frameCanvas.height);
      const frameUrl = frameCanvas.toDataURL('image/png');

      await addImageFromUrl(frameUrl);

      const canvas = canvasRef.current;
      const active = canvas?.getActiveObject();
      if (active) {
        active.set('mpMediaType', 'video');
        active.set('mpSourceUrl', url);
      }

      setVideoSourceUrl(url);
      canvas?.requestRenderAll();
    } catch {
      setError('Falha ao inserir vídeo no canvas.');
    }
  }, [addImageFromUrl]);

  useEffect(() => {
    if (!initialImageUrl || !canvasRef.current) return;
    addImageFromUrl(initialImageUrl);
  }, [addImageFromUrl, initialImageUrl]);

  const uploadImage = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      await addImageFromUrl(String(reader.result || ''));
    };
    reader.readAsDataURL(file);
  }, [addImageFromUrl]);

  const addMarketingLogo = useCallback(async () => {
    await addImageFromUrl('/logo-colorida.png');
  }, [addImageFromUrl]);

  const addDefaultLogo = useCallback(async () => {
    await addImageFromUrl('/logo-branca.png');
  }, [addImageFromUrl]);

  const bringForward = useCallback(() => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    canvas.bringObjectForward(active);
    canvas.requestRenderAll();
  }, []);

  const sendBackward = useCallback(() => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    canvas.sendObjectBackwards(active);
    canvas.requestRenderAll();
  }, []);

  const rotateSelected = useCallback((delta = 15) => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    active.rotate((active.angle || 0) + delta);
    canvas.requestRenderAll();
  }, []);

  const removeSelected = useCallback(() => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    canvas.remove(active);
    canvas.requestRenderAll();
    refreshLayers();
  }, [refreshLayers]);

  const selectLayer = useCallback((layerId) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const object = canvas.getObjects().find((item) => item.get('mpObjectId') === layerId);
    if (!object) return;

    canvas.setActiveObject(object);
    canvas.requestRenderAll();
    refreshLayers();
  }, [refreshLayers]);

  const waitForCanvasImages = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const imageObjects = canvas.getObjects().filter((object) => object.type === 'image');
    if (!imageObjects.length) return;

    await Promise.all(imageObjects.map((object) => new Promise((resolve) => {
      const element = object.getElement ? object.getElement() : object._element;
      if (!element) {
        resolve();
        return;
      }

      if (element.complete) {
        resolve();
        return;
      }

      element.addEventListener('load', () => resolve(), { once: true });
      element.addEventListener('error', () => resolve(), { once: true });
    })));
  }, []);

  const exportPng = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const hasVideo = canvas.getObjects().some((object) => object.get('mpMediaType') === 'video');
    if (hasVideo) {
      setError('Exportação PNG desativada para canvas com vídeo. Use download do vídeo original.');
      return;
    }

    if (document.fonts?.ready) {
      await document.fonts.ready;
    }

    await waitForCanvasImages();

    const png = canvas.toDataURL({ format: 'png', multiplier: 1.5 });
    const anchor = document.createElement('a');
    anchor.href = png;
    anchor.download = `marketingpro-design-${Date.now()}.png`;
    anchor.click();
  }, [waitForCanvasImages]);

  const downloadVideo = useCallback(() => {
    if (!videoSourceUrl) return;
    const anchor = document.createElement('a');
    anchor.href = videoSourceUrl;
    anchor.target = '_blank';
    anchor.rel = 'noreferrer';
    anchor.click();
  }, [videoSourceUrl]);

  const saveCanvas = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !designId) return;

    setIsSaving(true);
    setError('');
    try {
      await manualEditorService.updateCanvas(designId, canvas.toJSON(['mpObjectId', 'mpRole', 'src']));
    } catch {
      setError('Não foi possível salvar o JSON do canvas.');
    } finally {
      setIsSaving(false);
    }
  }, [designId]);

  return {
    canvasElementRef,
    isReady,
    isSaving,
    isLoading,
    error,
    layers,
    activeLayerId,
    textStyle,
    applyTextStyle,
    selectedPhotoMedia,
    setSelectedPhotoMedia,
    selectedVideoMedia,
    setSelectedVideoMedia,
    videoSourceUrl,
    designId,
    addText,
    addImageFromUrl,
    uploadImage,
    addMarketingLogo,
    addDefaultLogo,
    bringForward,
    sendBackward,
    rotateSelected,
    removeSelected,
    selectLayer,
    saveCanvas,
    exportPng,
    addVideoFromUrl,
    downloadVideo,
  };
}
